import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Lock, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MasterLockScreen({ onUnlock }) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleDigit = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    setError('');

    if (value && index < 3) {
      const next = document.getElementById(`master-pin-${index + 1}`);
      if (next) next.focus();
    }

    if (value && index === 3) {
      const pin = [...newDigits.slice(0, 3), value].join('');
      if (pin.length === 4) {
        verifyPin(pin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const prev = document.getElementById(`master-pin-${index - 1}`);
      if (prev) prev.focus();
    }
  };

  const verifyPin = async (pin) => {
    setIsVerifying(true);
    try {
      const res = await axios.post(`${API}/master-lock/verify`, { pin });
      if (res.data.valid) {
        sessionStorage.setItem('noyo_master_unlocked', 'true');
        onUnlock();
      } else {
        setError('Access denied');
        setDigits(['', '', '', '']);
        setTimeout(() => {
          const first = document.getElementById('master-pin-0');
          if (first) first.focus();
        }, 100);
      }
    } catch (err) {
      setError('Verification failed');
      setDigits(['', '', '', '']);
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    // Focus first input after a short delay
    setTimeout(() => {
      const first = document.getElementById('master-pin-0');
      if (first) first.focus();
    }, 500);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-background z-[200] flex flex-col items-center justify-center"
      data-testid="master-lock-screen"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />
      </div>

      <div className="relative z-10 text-center">
        <div className="mb-10">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
            <BookOpen className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-serif font-medium text-foreground tracking-tight">NOYO</h1>
          <p className="text-sm text-muted-foreground mt-3">Under Maintenance</p>
          <p className="text-xs text-muted-foreground mt-1">Be back soon!</p>
        </div>

        <div className="flex items-center justify-center space-x-2 mb-8">
          <Lock className="w-4 h-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Admin access</p>
        </div>

        <div className="flex justify-center space-x-4 mb-6">
          {[0, 1, 2, 3].map(i => (
            <input
              key={i}
              id={`master-pin-${i}`}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={digits[i]}
              onChange={(e) => handleDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={isVerifying}
              className="w-14 h-16 text-center text-2xl font-bold bg-card border-2 border-border rounded-2xl focus:outline-none focus:border-primary transition-colors disabled:opacity-50"
              data-testid={`master-pin-digit-${i}`}
            />
          ))}
        </div>

        {error && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-red-500 mb-4"
            data-testid="master-lock-error"
          >
            {error}
          </motion.p>
        )}

        {isVerifying && (
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        )}

        <p className="mt-12 text-xs text-muted-foreground/50 max-w-xs mx-auto">
          We're making things better for you.
          <br />
          Check back shortly!
        </p>
      </div>
    </motion.div>
  );
}

export default MasterLockScreen;
